import React, { useState } from 'react';

import { styled } from '@mui/material/styles';
import {
    Box,
    Button,
    Card,
    Divider,
    Grid,
    Link,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { KeyValue } from 'common/components';
import { numberFormat } from 'common/utils/helpers';
import { getIsRedGirasol } from 'common/utils/session';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(
    ({ theme }) => `
    & .MuiToggleButton-secondary {
        color: ${theme.palette.mode === 'dark' ? '#fff' : '#002438'};
        padding: 6px 20px;

        &.Mui-selected {
            background-color: #002438;
            color: #fff;

            & :hover: {
                color: #002438;
            }
        }
    }
`,
);

const RequestDetails = ({
    handleOnShowModal,
    isKam,
    parameters,
    typeAlgorithm,
}) => {
    const { t } = useTranslation();

    const [typeHitch, setTypeHitch] = useState('percentage');
    const handleChange = (event, newValue) => {
        setTypeHitch(newValue);
    };
    const isRedGirasol = getIsRedGirasol();
    const {
        amount = 0,
        annual_increase = 0,
        dealer_fee = 0,
        dealer_fee_amount = 0,
        hitch_amount = 0,
        hitch_percentage = 0,
        loan_amount = 0,
        metadata = {},
        ppa_amount = 0,
        rate = 0,
        term = 12,
    } = parameters;

    return (
        <React.Fragment>
            <Card.Body>
                <Box hidden={isKam}>
                    <Grid container>
                        <Grid size={{ md: 9, xs: 18 }}>
                            <KeyValue
                                append="mxn"
                                title={t('System cost')}
                                value={numberFormat(amount, {
                                    style: 'currency',
                                })}
                            />
                        </Grid>

                        <Grid size={{ md: 9, xs: 18 }}>
                            <KeyValue
                                append="mxn"
                                title={t('Total to finance')}
                                value={numberFormat(loan_amount, {
                                    style: 'currency',
                                })}
                            />
                        </Grid>

                        <Grid size={{ md: 9, xs: 18 }}>
                            <KeyValue
                                append="%"
                                title={`${t('Dealer fee')} %`}
                                value={numberFormat(dealer_fee, {
                                    style: 'decimal',
                                })}
                            />
                        </Grid>

                        <Grid size={{ md: 9, xs: 18 }}>
                            <KeyValue
                                append="mxn"
                                title={t('Price with dealer fee')}
                                value={numberFormat(dealer_fee_amount, {
                                    style: 'currency',
                                })}
                            />
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid size={{ md: 9, xs: 18 }}>
                            {typeHitch === 'amount' && (
                                <KeyValue
                                    append="mxn"
                                    title={`${t('Hitch')} ($)`}
                                    value={numberFormat(hitch_amount, {
                                        style: 'currency',
                                    })}
                                />
                            )}
                            {typeHitch === 'percentage' && (
                                <KeyValue
                                    append="%"
                                    title={`${t('Hitch')} (%)`}
                                    value={numberFormat(hitch_percentage, {
                                        style: 'decimal',
                                    })}
                                />
                            )}
                        </Grid>

                        <Grid size={{ md: 9, xs: 18 }}>
                            <StyledToggleButtonGroup
                                color="secondary"
                                exclusive
                                onChange={handleChange}
                                size="small"
                                value={typeHitch}
                            >
                                <ToggleButton
                                    value="percentage"
                                    sx={{ width: 30 }}
                                >
                                    %
                                </ToggleButton>
                                <ToggleButton value="amount" sx={{ width: 30 }}>
                                    $
                                </ToggleButton>
                            </StyledToggleButtonGroup>
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid size={{ md: 9, xs: 18 }}>
                            <KeyValue
                                append="%"
                                title={t('Rate', { context: 'financial' })}
                                value={rate}
                            />
                        </Grid>

                        <Grid size={{ md: 6, xs: 18 }}>
                            <KeyValue
                                append={t('Month', { count: term })}
                                title={t('Term')}
                                value={term}
                            />
                        </Grid>
                    </Grid>
                </Box>

                {isKam && (
                    <Grid container>
                        {typeAlgorithm === 'ppa_installment' && (
                            <>
                                <Grid size={{ md: 7, xs: 18 }}>
                                    <KeyValue
                                        title={t('Capex to finance')}
                                        value={numberFormat(metadata?.capex, {
                                            decimals: 2,
                                            style: 'currency',
                                        })}
                                    />
                                </Grid>
                                <Grid size={{ md: 7, xs: 18 }}>
                                    <KeyValue
                                        title={t('System size')}
                                        value={numberFormat(
                                            metadata?.system_size,
                                            {
                                                decimals: 2,
                                                style: 'decimal',
                                                unit: 'kW',
                                            },
                                        )}
                                    />
                                </Grid>
                            </>
                        )}

                        {typeAlgorithm !== 'ppa_installment' && (
                            <Grid size={{ md: 4, xs: 18 }}>
                                <KeyValue
                                    title={t('Price')}
                                    value={numberFormat(ppa_amount, {
                                        style: 'currency',
                                    })}
                                />
                            </Grid>
                        )}

                        <Grid size={{ md: 4, xs: 18 }}>
                            <KeyValue
                                append={t('Month', { count: term })}
                                title={t('Term')}
                                value={term}
                            />
                        </Grid>

                        <Grid size={{ md: 8, xs: 18 }}>
                            <KeyValue
                                append="%"
                                title={t('Annual percentage increase')}
                                value={annual_increase}
                            />
                        </Grid>

                        {typeAlgorithm === 'ppa_installment' && (
                            <>
                                <Grid size={{ md: 4, xs: 18 }}>
                                    <KeyValue
                                        title={t('Exchange rate')}
                                        value={numberFormat(
                                            metadata?.exchange_rate,
                                            {
                                                decimals: 2,
                                                style: 'currency',
                                            },
                                        )}
                                    />
                                </Grid>

                                <Grid size={{ md: 4, xs: 18 }}>
                                    <KeyValue
                                        title={t('O&M Pricing')}
                                        value={numberFormat(
                                            metadata?.work_cost_amount,
                                            {
                                                decimals: 2,
                                                style: 'currency',
                                            },
                                        )}
                                    />
                                </Grid>

                                <Grid size={{ md: 8, xs: 18 }}>
                                    <KeyValue
                                        title={t('Months of work completion')}
                                        value={metadata?.work_cost_month}
                                    />
                                </Grid>

                                <Grid size={{ md: 4, xs: 18 }}>
                                    <KeyValue
                                        append="%"
                                        title={t('Annual rate')}
                                        value={numberFormat(
                                            metadata?.annual_interest_rate,
                                            {
                                                decimals: 2,
                                                style: 'decimal',
                                            },
                                        )}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                )}
                {isRedGirasol && (
                    <>
                        <Typography fontWeight="bold" variant="body2" mt={2}>
                            Link de seguimiento
                        </Typography>
                        <Typography variant="body2" mb={2}>
                            <Link
                                href={metadata?.follow_up_url}
                                target="_blank"
                            >
                                {metadata?.follow_up_url}
                            </Link>
                        </Typography>
                    </>
                )}
            </Card.Body>

            <Divider />

            <Card.Body sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                    color="secondary"
                    onClick={handleOnShowModal}
                    variant="text"
                >
                    {t('See financial run')}
                </Button>
            </Card.Body>
        </React.Fragment>
    );
};

RequestDetails.propTypes = {
    handleOnShowModal: PropTypes.func,
    isKam: PropTypes.bool,
    parameters: PropTypes.object,
    typeAlgorithm: PropTypes.string,
};

export default RequestDetails;
